import { VideoBackground } from '@/features/Video';
import { getFallbackImageVideo } from '@/utils/resolvers/resolveVideo';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import Image from '../Image';
import { ContentContainerProps } from './ContentContainer.interface';
import useStyles from './ContentContainer.styles';

const ContentContainer = ({ ...props }: PropsWithChildren<ContentContainerProps>) => {
  const {
    // eslint-disable-next-line react/destructuring-assignment
    mobileBottomBackgroundColor = props.bottomBackgroundColor,
    // eslint-disable-next-line react/destructuring-assignment
    mobileTopBackgroundColor = props.topBackgroundColor,
    topSpacing,
    mobileTopSpacing,
    bottomSpacing,
    mobileBottomSpacing,
    topBackgroundColor,
    bottomBackgroundColor,
    backgroundVideo,
    className: overrideClassName,
    children,
    backgroundImage: bgImage,
    mobileBackgroundImage,
    videoContainerClass,
    multipleBackground = false,
    mobileMultipleBackground = false,
    multipleBackgroundSelected = 0,
    multipleBackgroundImage: bgImages = [],
    offsetTop,
    insetTop,
    overlay,
    topBackgroundStyles = {},
    bottomBackgroundStyles = {},
    isBackgroundSticky,
    datalayerPosition = '',
    datalayerVariant = '',
    ...restProps
  } = props;
  const classes = useStyles({});
  const muiTheme = useTheme();
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const bgVideo = useMemo(
    () => ({
      ...backgroundVideo,
      fallbackImage: getFallbackImageVideo({
        isMobile,
        mobileBackgroundImage,
        fallbackImage: backgroundVideo?.fallbackImage,
      }),
    }),
    [backgroundVideo, isMobile, mobileBackgroundImage],
  );
  const image = useMemo(
    () => (isMobile ? mobileBackgroundImage : bgImage),
    [bgImage, isMobile, mobileBackgroundImage],
  );
  const displayMultipleBackground = useMemo(
    () => (isMobile ? mobileMultipleBackground : multipleBackground),
    [isMobile, mobileMultipleBackground, multipleBackground],
  );
  const displayMultipleBgImage = useMemo(
    () =>
      displayMultipleBackground && bgImages.length > 0 && bgImages.find((bg) => bg !== undefined),
    [displayMultipleBackground, bgImages],
  );
  const displaySingleBgImage = useMemo(
    () => !displayMultipleBgImage && image,
    [displayMultipleBgImage, image],
  );
  const displayBgImage = useMemo(
    () => displayMultipleBgImage || displaySingleBgImage,
    [displayMultipleBgImage, displaySingleBgImage],
  );
  const displayBgVideo = useMemo(
    () => !displayBgImage && bgVideo?.videoUrl,
    [bgVideo?.videoUrl, displayBgImage],
  );
  const backgroundContainerClass = classes.backgroundContainer;

  useEffect(() => {
    if (imageContainerRef?.current) {
      const images = imageContainerRef.current.querySelectorAll('.is-multiple');
      images.forEach((img, idx) => {
        const shouldHide = multipleBackgroundSelected !== idx;
        img.classList.toggle('is-hidden', shouldHide);
        img.classList.toggle('is-visible', !shouldHide);
      });
    }
  }, [multipleBackgroundSelected, imageContainerRef]);

  const rootClassName = clsx(
    classes.root,
    offsetTop && 'offset-top',
    insetTop && `inset-${insetTop}`,
    topSpacing && `top-${topSpacing}-md`,
    (mobileTopSpacing || topSpacing) && `top-${mobileTopSpacing || topSpacing}`,
    bottomSpacing && `bottom-${bottomSpacing}-md`,
    (mobileBottomSpacing || bottomSpacing) && `bottom-${mobileBottomSpacing || bottomSpacing}`,
    !insetTop && (displayBgVideo || displayBgImage) && 'bottom-none',
    overlay?.hasTextShadow && `${overlay.alignment || 'left'}-overlay`,
    overlay?.opacity && `overlay-opacity-${overlay.opacity}`,
    overlay?.maxWidth && `max-width-${overlay?.maxWidth}`,
    overrideClassName,
    isBackgroundSticky && 'is-sticky',
  );

  const bgWrapperClassName = clsx('container-background', classes.background);
  const topBackgroundClassName = clsx(
    'top-background',
    classes.topBackground,
    `bg-color-${mobileTopBackgroundColor}`,
    `bg-color-${topBackgroundColor}-md`,
  );

  const bottomBackgroundClassName = clsx(
    'bottom-background',
    classes.bottomBackground,
    `bg-color-${mobileBottomBackgroundColor}`,
    `bg-color-${bottomBackgroundColor}-md`,
  );

  return (
    <div
      {...restProps}
      className={rootClassName}
      data-testid="qa-container"
      ref={imageContainerRef}
      {...(!!datalayerPosition && { 'data-datalayer-position': datalayerPosition })}
      {...(!!datalayerVariant && { 'data-datalayer-variant': datalayerVariant })}
    >
      <div className={bgWrapperClassName}>
        <div className={topBackgroundClassName} style={topBackgroundStyles}>
          {displayBgVideo && (
            <div
              className={clsx(
                backgroundContainerClass,
                classes.backgroundVideoContainer,
                videoContainerClass,
              )}
            >
              <VideoBackground
                videoUrl={bgVideo.videoUrl}
                isFullBleed={/fullbleed/i.test(bgVideo.presentation || '')}
                fallbackImage={bgVideo.fallbackImage}
                className={classes.backgroundVideo}
              />
            </div>
          )}
          {displayBgImage && (
            <div
              className={clsx(backgroundContainerClass, classes.backgroundImageContainer)}
              data-testid="qa-container-background-image-video"
            >
              {displayMultipleBgImage &&
                bgImages.map((bg, idx) =>
                  bg ? (
                    <Image
                      image={bg || image}
                      className={clsx(
                        classes.backgroundImage,
                        'is-multiple',
                        multipleBackgroundSelected === idx ? 'is-visible' : 'is-hidden',
                      )}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${bg.original?.url}_${idx}`}
                      type="background"
                      size="cover"
                      roundedCorners={false}
                    />
                  ) : (
                    <div
                      className={clsx(
                        classes.backgroundImage,
                        'is-multiple',
                        multipleBackgroundSelected === idx ? 'is-visible' : 'is-hidden',
                      )}
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                    />
                  ),
                )}
              {displaySingleBgImage && image && (
                <Image
                  image={image}
                  className={classes.backgroundImage}
                  type="background"
                  size="cover"
                  roundedCorners={false}
                />
              )}
            </div>
          )}
          <div className={classes.overlay} />
        </div>
        <div className={bottomBackgroundClassName} style={bottomBackgroundStyles} />
      </div>
      <div className={clsx(classes.content, 'content-container__content')}>{children}</div>
    </div>
  );
};

export default memo(ContentContainer);
